import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect } from "react";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CadFileIcon from './CadFileIcon';

export default function DownloadImageDialog({
  open,
  onClose,
  imageUrl,
  onDownload,
  loading,
  imageType,
  imageName,
}: {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  onDownload: () => void;
  loading: boolean;
  imageType: string;
  imageName: string;
}) {
  useEffect(() => {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) {
      activeElement.blur();
    }
  }, [open]);

  // Check if file is an image or CAD file
  const getFileExtension = (filename: string): string => {
    return filename.toLowerCase().substring(filename.lastIndexOf('.'));
  };

  const isImageFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].includes(ext);
  };

  const isCADFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      '.dwg', '.dxf', '.vwx', '.jww', '.jwc', 
      '.sfc', '.p21', '.sfz', '.p2z', '.bfo',
      '.tfv', '.dvv', '.dvm', '.bvf',
      '.zrd', '.zfd', '.zsd', '.zsdx'
    ].includes(ext);
  };

  const fileIsImage = imageName && isImageFile(imageName);
  const fileIsCAD = imageName && isCADFile(imageName);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {fileIsCAD ? 'CADファイルをダウンロード' : '図面ファイルを表示'}
      </DialogTitle>
      <DialogContent dividers>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          {loading ? (
            <CircularProgress />
          ) : imageUrl ? (
            <div style={{ width: "100%" }}>
              {fileIsImage ? (
                // Display image preview
                <div>
                  <div className="flex justify-center items-center">
                    <img
                      src={`data:${imageType};base64,${imageUrl}`}
                      alt="図面プレビュー"
                      style={{ maxWidth: "100%", maxHeight: "500px" }}
                    />
                  </div>
                  <div className="flex justify-center items-center mt-3">
                    <p style={{ fontWeight: 500 }}>{imageName}</p>
                  </div>
                </div>
              ) : fileIsCAD ? (
                // Display CAD file info (no preview)
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  border: '2px solid #1976d2'
                }}>
                  <CadFileIcon 
                    sx={{ 
                      fontSize: 80, 
                      color: '#1976d2',
                      marginBottom: '20px'
                    }} 
                  />
                  <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '10px' }}>
                    CADファイル
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#1976d2',
                    wordBreak: 'break-all',
                    marginBottom: '20px',
                    padding: '0 20px'
                  }}>
                    {imageName}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    backgroundColor: '#fff',
                    padding: '15px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    <strong>📋 形式:</strong> {getFileExtension(imageName).toUpperCase().replace('.', '')}
                    <div style={{ marginTop: '10px' }}>
                      CADファイルはプレビューできません。<br />
                      ダウンロードしてCADソフトウェアで開いてください。
                    </div>
                  </div>
                </div>
              ) : (
                // Unknown file type
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px'
                }}>
                  <InsertDriveFileIcon 
                    sx={{ 
                      fontSize: 64, 
                      color: '#757575',
                      marginBottom: '20px'
                    }} 
                  />
                  <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                    {imageName}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    ダウンロードボタンをクリックしてファイルを取得してください。
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999' }}>
              ファイルがありません
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          キャンセル
        </Button>
        <Button 
          variant="contained" 
          onClick={onDownload}
          disabled={!imageUrl || loading}
        >
          ダウンロード
        </Button>
      </DialogActions>
    </Dialog>
  );
}
